import { Zap, Search, ShieldCheck, MessageSquare, Target, BarChart2, ArrowRight, Star } from 'lucide-react';

const FEATURES = [
  { icon: Search, title: 'Hunt', desc: 'Curated X/Twitter search queries to find active Web3 projects with budget — updated for 2026.' },
  { icon: ShieldCheck, title: 'Qualify Fast', desc: 'Green and red flag checklist. Know in 60 seconds if a project is worth pitching.' },
  { icon: MessageSquare, title: 'DM Scripts', desc: '18+ battle-tested pitch templates for every Web3 gig — KOL, social media, community, growth.' },
  { icon: Zap, title: 'AI Pitch Writer', desc: 'Enter a project name and get a personalized cold DM written by Claude AI in seconds.' },
  { icon: Target, title: 'Pitch Tracker', desc: 'Log every pitch, track status, and monitor earnings. Goal: 20 pitches a night.' },
  { icon: BarChart2, title: 'Analytics', desc: 'See your reply rate, close rate, best scripts, and best platforms — all in one dashboard.' },
];

const STEPS = [
  { step: '01', title: 'Hunt for projects', desc: 'Use built-in X search queries to find active Web3 projects posting about launches today.' },
  { step: '02', title: 'Qualify in 60 seconds', desc: 'Run through the green/red flag checklist. Skip bad projects fast.' },
  { step: '03', title: 'Send a personalized pitch', desc: 'Pick a script or let AI write a custom DM. Copy and send.' },
  { step: '04', title: 'Track and close', desc: 'Log every pitch, follow up, and watch the analytics improve over time.' },
];

export default function LandingPage({ onEnter }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'var(--font-body)' }}>

      {/* Nav */}
      <nav style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '1.25rem 2.5rem', borderBottom: '1px solid var(--border)',
        position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Zap size={16} color="#000" fill="#000" />
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 18, letterSpacing: '-0.02em' }}>W3Pitch</div>
        </div>
        <button onClick={onEnter} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'var(--accent)', color: '#000', border: 'none',
          padding: '8px 20px', borderRadius: 'var(--radius)',
          fontSize: 14, fontWeight: 600, cursor: 'pointer',
          fontFamily: 'var(--font-body)',
        }}>
          Open App <ArrowRight size={14} />
        </button>
      </nav>

      {/* Hero */}
      <section style={{ textAlign: 'center', padding: '6rem 2rem 4rem', maxWidth: 720, margin: '0 auto' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'var(--accent-dim)', border: '1px solid var(--accent-dim2)',
          borderRadius: 20, padding: '5px 14px', marginBottom: '1.5rem',
          fontSize: 12, color: 'var(--accent)', fontFamily: 'var(--font-mono)',
        }}>
          <Zap size={11} fill="var(--accent)" /> Web3 Freelancer Toolkit
        </div>

        <h1 style={{
          fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 6vw, 64px)',
          fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.1,
          marginBottom: '1.5rem', color: 'var(--text)',
        }}>
          Land Web3 clients.<br />
          <span style={{ color: 'var(--accent)' }}>Tonight.</span>
        </h1>

        <p style={{
          fontSize: 18, color: 'var(--text2)', lineHeight: 1.7,
          marginBottom: '2.5rem', maxWidth: 540, margin: '0 auto 2.5rem',
        }}>
          W3Pitch is a full hunting and pitching system for Web3 freelancers. Find active projects, qualify them fast, send personalized pitches, and track everything — all in one place.
        </p>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={onEnter} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--accent)', color: '#000', border: 'none',
            padding: '14px 32px', borderRadius: 'var(--radius)',
            fontSize: 16, fontWeight: 700, cursor: 'pointer',
            fontFamily: 'var(--font-display)', letterSpacing: '-0.01em',
          }}>
            Start Hunting <ArrowRight size={16} />
          </button>
          <button onClick={onEnter} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'transparent', color: 'var(--text2)',
            border: '1px solid var(--border2)',
            padding: '14px 32px', borderRadius: 'var(--radius)',
            fontSize: 16, cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}>
            See how it works
          </button>
        </div>

        {/* Social proof */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: '2rem' }}>
          {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="var(--amber)" color="var(--amber)" />)}
          <span style={{ fontSize: 13, color: 'var(--text2)', marginLeft: 4 }}>Built for Web3 freelancers who need results fast</span>
        </div>
      </section>

      {/* App preview mockup */}
      <section style={{ padding: '0 2rem 5rem', maxWidth: 900, margin: '0 auto' }}>
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
        }}>
          {/* Fake browser bar */}
          <div style={{
            background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
            padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' }} />
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e' }} />
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840' }} />
            <div style={{
              marginLeft: 12, background: 'var(--bg3)', borderRadius: 6,
              padding: '3px 16px', fontSize: 12, color: 'var(--text3)',
              fontFamily: 'var(--font-mono)',
            }}>w3pitch.vercel.app</div>
          </div>
          {/* Mock app UI */}
          <div style={{ display: 'flex', height: 340 }}>
            <div style={{ width: 160, background: 'var(--bg2)', borderRight: '1px solid var(--border)', padding: '1rem 0.75rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', marginBottom: 8 }}>
                <div style={{ width: 24, height: 24, borderRadius: 6, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Zap size={12} color="#000" fill="#000" />
                </div>
                <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 13 }}>W3Pitch</span>
              </div>
              {[
                { icon: Search, label: 'Hunt', active: true },
                { icon: ShieldCheck, label: 'Qualify' },
                { icon: MessageSquare, label: 'Scripts' },
                { icon: Zap, label: 'AI Pitch' },
                { icon: Target, label: 'Tracker' },
                { icon: BarChart2, label: 'Analytics' },
              ].map(({ icon: Icon, label, active }) => (
                <div key={label} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 10px', borderRadius: 8,
                  background: active ? 'var(--accent-dim)' : 'transparent',
                  color: active ? 'var(--accent)' : 'var(--text3)',
                  fontSize: 12,
                }}>
                  <Icon size={13} /> {label}
                  {label === 'AI Pitch' && <span style={{ marginLeft: 'auto', fontSize: 8, background: 'var(--accent-dim)', color: 'var(--accent)', padding: '1px 5px', borderRadius: 8 }}>AI</span>}
                </div>
              ))}
            </div>
            <div style={{ flex: 1, padding: '1.5rem', overflow: 'hidden' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>X / Twitter search queries</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                {['"token launch" lang:en', '"NFT mint" today', '"TGE" "2026"', '"hiring" "community manager" web3', '"airdrop" "KOL" -scam', '"growth" "marketing" "web3"'].map(q => (
                  <span key={q} style={{
                    background: 'var(--bg3)', border: '1px solid var(--border2)',
                    borderRadius: 6, padding: '4px 10px',
                    fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text2)',
                  }}>{q}</span>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>How to find the founder's DM</div>
              {['Find the project tweet', 'Check who posted it', 'Go to their replies', 'Check Discord / Telegram in bio'].map((step, i) => (
                <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--accent)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>0{i+1}</div>
                  <div style={{ fontSize: 12, color: 'var(--text2)' }}>{step}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: '4rem 2rem', maxWidth: 960, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Everything you need</div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 800, letterSpacing: '-0.03em' }}>One tool. Full pipeline.</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: '1.5rem',
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                <Icon size={16} color="var(--accent)" />
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{title}</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section style={{ padding: '4rem 2rem', maxWidth: 800, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>The process</div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 800, letterSpacing: '-0.03em' }}>How it works</h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {STEPS.map(({ step, title, desc }) => (
            <div key={step} style={{
              display: 'flex', gap: 20, alignItems: 'flex-start',
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem',
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--accent)', fontWeight: 600,
              }}>{step}</div>
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{title}</div>
                <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '4rem 2rem 6rem', textAlign: 'center' }}>
        <div style={{
          maxWidth: 560, margin: '0 auto',
          background: 'var(--surface)', border: '1px solid var(--accent-dim2)',
          borderRadius: 'var(--radius-lg)', padding: '3rem 2rem',
        }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
            <Zap size={22} color="#000" fill="#000" />
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 12 }}>Ready to land your first gig?</h2>
          <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.7, marginBottom: '2rem' }}>
            Free to use. No account needed. Start hunting Web3 projects right now.
          </p>
          <button onClick={onEnter} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'var(--accent)', color: '#000', border: 'none',
            padding: '14px 36px', borderRadius: 'var(--radius)',
            fontSize: 16, fontWeight: 700, cursor: 'pointer',
            fontFamily: 'var(--font-display)', letterSpacing: '-0.01em',
          }}>
            Open W3Pitch <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        borderTop: '1px solid var(--border)', padding: '1.5rem 2.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      }}>
        <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>built by</span>
        <span style={{ fontSize: 12, fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--accent)' }}>adee</span>
        <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>— Web3 Growth Marketer</span>
      </footer>
    </div>
  );
}